import { ShoppingBasket } from 'lucide-react';
import { Link } from 'react-router-dom';
import { listDocName, type ListItem, type ListMeta } from '@awwyeah/shared';
import { useYArray, useYDoc, useYMap } from '../../sync';
import { ListItemRow } from '../lists/components/ListItemRow';
import { getOrderedItems, toggleItem } from '../lists/listOps';
import { useHouseholdRoster } from '../lists/useHouseholdRoster';
import { DIGEST_SECTION_CAP, MoreLink } from './MoreLink';

/**
 * The household's main groceries list, pinned to the digest (Dan, 2026-08-07): the morning
 * glance is "what's today, what do we need". Matched by name rather than a pinned flag —
 * every household calls it "Groceries", and a rename-to-match beats a new meta field.
 */
export function findGroceriesList(lists: ListMeta[]): ListMeta | null {
  const active = lists.filter((list) => !list.archived);
  return (
    active.find((list) => list.name.trim().toLowerCase() === 'groceries') ??
    active.find((list) => list.name.toLowerCase().includes('grocer')) ??
    null
  );
}

/**
 * A live, checkable slice of the groceries list embedded in the digest — the same
 * `ListItemRow` + `toggleItem` the list page uses, minus editing/assigning/reordering,
 * so a check-off here is the real thing, not a preview. Headers and done items stay on
 * the full list page; the digest shows only what still needs buying.
 */
export function GroceriesCard({ list, userId }: { list: ListMeta; userId: string }): JSX.Element {
  const { getMember, meId } = useHouseholdRoster();
  const doc = useYDoc(listDocName(list.id));
  const { array: order } = useYArray<string>(doc, 'order');
  const { map } = useYMap<ListItem>(doc, 'items');
  const open = getOrderedItems(order, map).filter((item) => !item.header && !item.checked);
  // A full shop lands 30+ items at once; the digest shows the top of the list and hands
  // off to the list page for the rest (Dan, 2026-08-18). Order is the list's own, so
  // "the first five" is the same five everyone sees at the top of Groceries.
  const shown = open.slice(0, DIGEST_SECTION_CAP);

  return (
    <section className="flex flex-col gap-2" data-testid="digest-groceries">
      <div className="flex items-center gap-2">
        <ShoppingBasket className="h-4 w-4 text-accent" aria-hidden="true" />
        <h2 className="text-[11px] font-semibold uppercase tracking-[0.08em] text-fg-3">
          {list.emoji ? `${list.emoji} ` : ''}
          {list.name}
        </h2>
        <Link
          to={`/lists/${list.id}`}
          className="ml-auto text-xs font-semibold text-accent"
          data-testid="digest-groceries-open"
        >
          Open list
        </Link>
      </div>
      {open.length === 0 ? (
        <p className="rounded-sm border border-dashed border-line px-3 py-2.5 text-sm text-fg-3">
          Nothing on the list — you're all stocked.
        </p>
      ) : (
        <>
          <div className="overflow-hidden rounded-sm border border-line">
            {shown.map((item) => (
              <ListItemRow
                key={item.id}
                item={item}
                variant="todo"
                getMember={getMember}
                meId={meId}
                onToggle={() => toggleItem(doc, map, item.id, userId)}
              />
            ))}
          </div>
          <MoreLink
            hidden={open.length - DIGEST_SECTION_CAP}
            to={`/lists/${list.id}`}
            label="to buy"
            testId="digest-more-groceries"
          />
        </>
      )}
    </section>
  );
}
